import React, { useContext, useState } from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";
import { Language } from "../../../App";
import { useNavigate } from "react-router-dom";
import { GenerateHelpTicketApi } from "../../../constants/API";
import { SETTINGMODULE } from "../../../constants/RoutesUrl";
import { authorize } from "../../../shared/authorize";
import { useCustomToast } from "../../common/CustomToast";
import { MdOutlineReport } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import axios from "axios";

const TOKEN = {
  primary: "#1DC1DD",
  primaryBg: "#E6F9FC",
  cardBg: "#ffffff",
  border: "#E5E7EB",
  textDark: "#0F1A2B",
  textGray: "#6B7280",
  font: "'Onest', sans-serif",
};

const inputStyle = (focused) => ({
  width: "100%",
  padding: "11px 14px",
  borderRadius: "12px",
  border: `1.5px solid ${focused ? TOKEN.primary : TOKEN.border}`,
  outline: "none",
  fontFamily: TOKEN.font,
  fontSize: "14px",
  color: TOKEN.textDark,
  backgroundColor: "#fff",
  boxShadow: focused ? "0 0 0 3px rgba(29,193,221,0.12)" : "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
});

const CreateComplaintModal = ({ isOpen, toggle, onSuccess }) => {
  const { lang } = useContext(Language);
  const navigate = useNavigate();
  const { showToast } = useCustomToast();

  const [helpDescription, setHelpDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectFocused, setSelectFocused] = useState(false);
  const [textareaFocused, setTextareaFocused] = useState(false);

  const isSaveButtonDisabled = !selectedCategory || !helpDescription.trim() || isLoading;

  const handleDescriptionChange = (e) => {
    setHelpDescription(e.target.value);
  };

  const handleClose = () => {
    setSelectedCategory("");
    setHelpDescription("");
    if (typeof toggle === "function") {
      toggle(false);
    }
  };

  const handleTicket = () => {
    const category = selectedCategory?.trim();
    const description = helpDescription?.trim();

    if (!category || !description) {
      showToast(
        lang?.please_select_category_enter_desc || "Please select a category and enter description",
        "error"
      );
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("type", category);
    formData.append("description", description);

    axios
      .post(GenerateHelpTicketApi, formData, {
        headers: {
          ...authorize().headers,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setIsLoading(false);
        const isSuccess =
          response.data?.status === true ||
          response.data?.result === true ||
          response.status === 200;

        if (isSuccess && response.data?.status !== false) {
          showToast(
            lang?.complaint_created_success || "Complaint submitted successfully!",
            "success"
          );
          handleClose();
          if (typeof onSuccess === "function") {
            onSuccess();
          } else {
            navigate(SETTINGMODULE + "/5");
          }
        } else {
          const errMsg =
            response.data?.errors?.[0] ||
            response.data?.action ||
            response.data?.message ||
            "Failed to submit complaint";
          showToast(errMsg, "error");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("API error:", error);
        const errMsg =
          error?.response?.data?.errors?.[0] ||
          error?.response?.data?.action ||
          error?.response?.data?.message ||
          "Failed to submit complaint. Please try again.";
        showToast(errMsg, "error");
      });
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      toggle={handleClose}
      zIndex={10050}
      contentClassName="chat-modal-content chat-module-theme border-0 overflow-hidden"
    >
      <ModalBody style={{ padding: "28px 26px" }}>
        {/* Modal Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                backgroundColor: TOKEN.primaryBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MdOutlineReport style={{ fontSize: "24px", color: TOKEN.primary }} />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "17px",
                  fontWeight: 700,
                  color: TOKEN.textDark,
                  fontFamily: TOKEN.font,
                }}
              >
                {lang?.create_complaint || "Create Complaint"}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "12.5px",
                  color: TOKEN.textGray,
                  fontFamily: TOKEN.font,
                  marginTop: "2px",
                }}
              >
                {lang?.help_support_sub || "Submit a ticket to support team"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="chat-drawer-close-btn"
            title="Dismiss"
          >
            <IoClose size={18} />
          </button>
        </div>

        <div style={{ height: "1px", backgroundColor: TOKEN.border, margin: "16px 0 20px 0" }} />

        <form onSubmit={(e) => e.preventDefault()}>
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontWeight: 600,
                fontSize: "13px",
                color: TOKEN.textDark,
                fontFamily: TOKEN.font,
                marginBottom: "7px",
              }}
            >
              {lang?.category || "Category"}
            </label>
            <select
              value={selectedCategory}
              onFocus={() => setSelectFocused(true)}
              onBlur={() => setSelectFocused(false)}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ ...inputStyle(selectFocused), appearance: "auto" }}
            >
              <option disabled value="">
                {lang?.select_category || "Select category"}
              </option>
              <option value={lang?.cancel_subscription || "Cancel Subscription"}>
                {lang?.cancel_subscription || "Cancel Subscription"}
              </option>
              <option value={lang?.refund_payment || "Refund Payment"}>
                {lang?.refund_payment || "Refund Payment"}
              </option>
              <option value={lang?.compl_coach || "Complaint on coach"}>
                {lang?.compl_coach || "Complaint on coach"}
              </option>
              <option value={lang?.videos_not_loading || "Videos not loading"}>
                {lang?.videos_not_loading || "Videos not loading"}
              </option>
              <option value={lang?.app_crashes || "App crashes"}>
                {lang?.app_crashes || "App crashes"}
              </option>
              <option value={lang?.help_support || "Help & Support"}>
                {lang?.help_support || "Help & Support"}
              </option>
            </select>
          </div>

          <div style={{ marginBottom: "22px" }}>
            <label
              style={{
                display: "block",
                fontWeight: 600,
                fontSize: "13px",
                color: TOKEN.textDark,
                fontFamily: TOKEN.font,
                marginBottom: "7px",
              }}
            >
              {lang?.description || "Description"}
            </label>
            <textarea
              rows="5"
              placeholder={lang?.enter_compl_her || "Enter your complaint here..."}
              value={helpDescription}
              onChange={handleDescriptionChange}
              onFocus={() => setTextareaFocused(true)}
              onBlur={() => setTextareaFocused(false)}
              style={{ ...inputStyle(textareaFocused), resize: "vertical", minHeight: "110px" }}
            />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={handleClose}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: "12px",
                border: `1.5px solid ${TOKEN.border}`,
                backgroundColor: "#fff",
                color: TOKEN.textDark,
                fontFamily: TOKEN.font,
                fontWeight: 700,
                fontSize: "14px",
                cursor: "pointer",
                transition: "background-color 0.15s",
              }}
            >
              {lang?.cancel || "Cancel"}
            </button>
            <button
              type="button"
              disabled={isSaveButtonDisabled}
              onClick={handleTicket}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: "12px",
                border: "none",
                backgroundColor: TOKEN.primary,
                color: "#fff",
                fontFamily: TOKEN.font,
                fontWeight: 700,
                fontSize: "14px",
                cursor: isSaveButtonDisabled ? "not-allowed" : "pointer",
                opacity: isSaveButtonDisabled ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "opacity 0.15s",
              }}
            >
              {isLoading ? <Spinner size="sm" /> : lang?.send_report || "Send Report"}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default CreateComplaintModal;
